import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Calendar, Clock, Check } from "lucide-react";
import { FaChair, FaDollarSign, FaUsers } from "react-icons/fa";
import { CheckCircle } from "react-feather";

import styles from "./CalendarReservation.module.css";
import { useAuth } from "../../context/AuthContext";

const SlotSelection = ({ facility, onSlotSelect }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [reservedSlots, setReservedSlots] = useState({});
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [calendarData, setCalendarData] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [availableSlots, setAvailableSlots] = useState([]);
  const [bookingData, setBookingData] = useState({});
  const { user } = useAuth();

  useEffect(() => {
    fetchCalendarData();
  }, [currentMonth, currentYear]);

  useEffect(() => {
    fetchAvailableSlots();
  }, [facility.id, selectedDate]);

  useEffect(() => {
    if (selectedDate) {
      fetchBookingData();
    }
  }, [selectedDate, facility.id]);

  const fetchCalendarData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/user-dashboard/fetch-calendar`,
        {
          params: { month: currentMonth, year: currentYear },
          withCredentials: true,
        }
      );
      setCalendarData(response.data);
    } catch (error) {
      console.error("Error fetching calendar data:", error);
    }
  };

  const fetchAvailableSlots = async () => {
    try {
      const params = { facilityId: facility.id };
      if (selectedDate) {
        params.selectedDate = selectedDate.toISOString().split("T")[0];
      }
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/user-dashboard/arena-available-slots`,
        { params, withCredentials: true }
      );
      setAvailableSlots(response.data);
    } catch (error) {
      console.error("Error fetching available slots:", error);
      setAvailableSlots([]);
    }
  };

  const fetchBookingData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/user-dashboard/slot-bookings`,
        {
          params: {
            facilityId: facility.id,
            date: selectedDate.toISOString().split("T")[0],
          },
          withCredentials: true,
        }
      );
      const bookings = {};
      response.data.forEach((booking) => {
        bookings[booking.time] = true;
      });
      setReservedSlots(bookings);
    } catch (error) {
      console.error("Error fetching booking data:", error);
      setReservedSlots({});
    }
  };

  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    return compareDate < today;
  };

  const canNavigateToPrevMonth = () => {
    const today = new Date();
    const firstDayOfCurrentMonth = new Date(currentYear, currentMonth - 1, 1);
    return (
      firstDayOfCurrentMonth >=
      new Date(today.getFullYear(), today.getMonth(), 1)
    );
  };

  const handlePrevMonth = () => {
    if (!canNavigateToPrevMonth()) return;
    if (currentMonth === 1) {
      setCurrentMonth(12);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 12) {
      setCurrentMonth(1);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleDateClick = (date) => {
    if (isPastDate(date)) return;
    const utcDate = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    );
    setSelectedDate(utcDate);
    setSelectedSlots([]);
  };
  const bottomRef = useRef(null);
  const handleSlotSelection = (slot) => {
    const userToken = user?.uid;
    const slotInfo = {
      user_hash: userToken,
      facility_id: facility.id,
      date: selectedDate.toISOString(),
      year: selectedDate.getFullYear(),
      month: selectedDate.getMonth() + 1,
      day: selectedDate.getDate(),
      time: slot.time,
      price: facility.charge || null,
    };
    setSelectedSlots((prev) => {
      const index = prev.findIndex(
        (s) => s.date === slotInfo.date && s.time === slot.time
      );
      if (index > -1) {
        return prev.filter((_, i) => i !== index);
      } else {
        return [...prev, slotInfo];
      }
    });
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };

  const isSlotSelected = (slot) => {
    return selectedSlots.some(
      (s) => s.date === selectedDate.toISOString() && s.time === slot.time
    );
  };

  const isSlotReserved = (slot) => {
    return reservedSlots[slot.time] || false;
  };

  const handleConfirmSelection = () => {
    onSlotSelect(selectedSlots);
  };

  const totalPrice = selectedSlots.reduce((sum, slot) => sum + (slot.price || 0), 0);

  return (
    <div className={styles.container}>
      <div className={styles.upper}>
        <div className={styles.calendarContainer}>
          <div className={styles.monthYearDisplay}>
            <button
              onClick={handlePrevMonth}
              disabled={!canNavigateToPrevMonth()}
              className={`${styles.navButton} ${
                canNavigateToPrevMonth() ? "" : styles.disabled
              }`}
            >
              &lt;
            </button>
            <div className={styles.monthYear}>
              <Calendar className={`${styles.icon} ${styles.animatePulse}`} />
              <span className={styles.month}>
                {calendarData[0]?.month_name}
              </span>
              <span className={styles.year}>{currentYear}</span>
            </div>
            <button onClick={handleNextMonth} className={styles.navButton}>
              &gt;
            </button>
          </div>
          <div className={styles.calendarGrid}>
            {calendarData.slice(0, 30).map((date) => {
              const dateObj = new Date(date.date);
              const isPast = isPastDate(dateObj);
              return (
                <button
                  key={date.id}
                  className={`${styles.dateButton} ${
                    selectedDate?.toDateString() === dateObj.toDateString()
                      ? styles.selected
                      : ""
                  } ${isPast ? styles.pastDate : ""}`}
                  onClick={() => handleDateClick(dateObj)}
                  disabled={isPast}
                >
                  {date.day}
                </button>
              );
            })}
          </div>
        </div>
        {selectedDate && (
          <div className={`${styles.timeSlots} ${styles.animateFadeIn}`}>
            <h2 className={styles.subtitle}>
              <Clock className={`${styles.icon} ${styles.animateSpinSlow}`} />
              Time Slots
            </h2>
            <div className={styles.slotsGrid}>
              {availableSlots.length > 0 ? (
                availableSlots.map((slot) => (
                  <button
                    key={slot.slot_id}
                    className={`${styles.slotButton} ${
                      isSlotReserved(slot) ? styles.reserved : ""
                    } ${isSlotSelected(slot) ? styles.selected : ""}`}
                    onClick={() => handleSlotSelection(slot)}
                    disabled={isSlotReserved(slot)}
                  >
                    {slot.time}
                  </button>
                ))
              ) : (
                <div
                  className={`${styles.noSlotsMessage} ${styles.animateFadeIn}`}
                >
                  <p>
                    No slots available for{" "}
                    {selectedDate.toLocaleDateString("en-US", {
                      weekday: "long",
                      month: "short",
                      day: "numeric",
                    })}
                    .
                  </p>
                  <p>
                    The arena owner may have disabled slots for this day, or
                    this facility is not available on{" "}
                    {selectedDate.toLocaleDateString("en-US", {
                      weekday: "long",
                    })}
                    s.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {selectedSlots.length > 0 && (
        <div
          ref={bottomRef}
          className={`${styles.selectedSlotsInfo} ${styles.animateSlideUp}`}
        >
          <div>
            <p>
              <FaChair style={{ color: "#0071bc" }} /> Slots:{" "}
              {selectedSlots.length}
            </p>
            <p>
              <FaUsers style={{ color: "#0071bc" }} /> Capacity:{" "}
              {facility.spectator_seating_capacity || "N/A"}
            </p>
            <p>
              <FaDollarSign style={{ color: "#0071bc" }} /> Rs. {totalPrice}
            </p>
          </div>
          <button
            className={styles.confirmButton}
            onClick={handleConfirmSelection}
          >
            <CheckCircle /> Confirm Slots
          </button>
        </div>
      )}
    </div>
  );
};

export default SlotSelection;
